import {
  Box,
  Chip,
  Modal,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import React from "react";
import { formatDateTime } from "../global/functions/dateFormat";
import { formatStatusText, getStatusColor } from "../models/status.model";
import { CouponType } from "../models/coupon.model";
import { useTranslation } from "react-i18next";

const HitoryModal = ({ data, open, handleClose }) => {
  const { t } = useTranslation();
  const discount = Number(data?.discountValue) || 0;
  const cashback = Number(data?.loyaltyDiscount) || 0;
  const subtotal = (Number(data?.totalHT) || 0) + (Number(data?.tvaAmount) || 0);
  const coupon =
    data?.couponId && typeof data.couponId === "object" ? data.couponId : null;
  const couponLabel = coupon?.code
    ? `${t("table.coupon")} ${coupon.code}`
    : t("table.coupon");
  const couponAmount =
    coupon?.couponType === CouponType.fixed
      ? `- ${discount.toFixed(2)} ${data?.currency || ""}`.trim()
      : `- ${discount} %`;
  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="history-modal">
      {data && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 500, md: 600 },
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            pt: 2,
            px: 4,
            pb: 3,
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <Box
            justifyContent="space-between"
            alignItems="center"
            flexDirection="row"
            display="flex"
            sx={{ mb: 2 }}
          >
            <Typography variant="h2" sx={{ fontWeight: 600 }}>
              Commande N°{data.commandNumber}
            </Typography>
            <Chip
              variant="outlined"
              label={formatStatusText(data?.status)}
              color={getStatusColor(data.status)}
            />
          </Box>
          <Box
            justifyContent="space-between"
            alignItems="center"
            flexDirection="row"
            display="flex"
            sx={{ mb: 2 }}
          >
            <Typography>Client: {data.name}</Typography>
            <Typography>{formatDateTime(data.boughtAt)}</Typography>
          </Box>
          <Box
            justifyContent="space-between"
            alignItems="center"
            flexDirection="row"
            display="flex"
            sx={{ mb: 4 }}
          >
            <Typography variant="h3" sx={{ fontWeight: 600 }}>
              Paiement via: {data.method.label}
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 600 }}>
              Pack: {data.pack.label}
            </Typography>
          </Box>
          <Box sx={{ margin: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>{t("table.product")}</TableCell>
                  <TableCell sx={{ fontWeight: 800 }} align="right">
                    Total ({data?.currency})
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.product.map((productRow, index) => (
                  <React.Fragment key={index}>
                    <TableRow>
                      <TableCell>
                        X{productRow.plat?.count} {productRow.plat?.name}
                      </TableCell>
                      <TableCell align="right">
                        {(productRow?.plat?.price * productRow.plat?.count).toFixed(2)}
                      </TableCell>
                    </TableRow>

                    {(productRow.addons.length > 0 || productRow.variation) && (
                      <TableRow>
                        <TableCell
                          style={{ paddingBottom: 0, paddingTop: 0 }}
                          colSpan={7}
                        >
                          <Table size="small">
                            <TableBody>
                              {productRow.variation && (
                                <TableRow key={index}>
                                  <TableCell>
                                    {productRow.variation.name}
                                  </TableCell>
                                  <TableCell align="right">
                                    {productRow.variation.price.toFixed(2) ||
                                      "Gratuit"}
                                  </TableCell>
                                </TableRow>
                              )}
                              {productRow.addons.map((addonRow, index) => (
                                <TableRow key={index}>
                                  <TableCell>
                                    X{addonRow.count} {addonRow.name}
                                  </TableCell>
                                  <TableCell align="right">
                                    {addonRow.price.toFixed(2) || "Gratuit"}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableCell>
                      </TableRow>
                    )}
                    {productRow.extras.length > 0 && (
                      <TableRow>
                        <TableCell
                          style={{ paddingBottom: 0, paddingTop: 0 }}
                          colSpan={7}
                        >
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 800 }}>
                                  {t("table.extra")}
                                </TableCell>
                                <TableCell></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {productRow.extras.map((extraRow, index) => (
                                <TableRow key={index}>
                                  <TableCell>
                                    X{extraRow.count} {extraRow.name}
                                  </TableCell>
                                  <TableCell align="right">
                                    {extraRow.price.toFixed(2)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
                <TableRow>
                  <TableCell sx={{ fontSize: "16px" }}>
                    TVA({data.tva}%)
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: "16px" }}>
                    {data.tvaAmount.toFixed(2)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontSize: "16px" }}>{t("table.totalHt")}</TableCell>
                  <TableCell align="right" sx={{ fontSize: "16px" }}>
                    {data.totalHT.toFixed(2)}
                  </TableCell>
                </TableRow>
                {(discount > 0 || cashback > 0) && (
                  <TableRow>
                    <TableCell sx={{ fontSize: "16px" }}>{t("table.subtotal")}</TableCell>
                    <TableCell align="right" sx={{ fontSize: "16px" }}>
                      {subtotal.toFixed(2)}
                    </TableCell>
                  </TableRow>
                )}
                {discount > 0 && (
                  <TableRow>
                    <TableCell sx={{ fontSize: "16px" }}>{couponLabel}</TableCell>
                    <TableCell align="right" sx={{ fontSize: "16px" }}>
                      {couponAmount}
                    </TableCell>
                  </TableRow>
                )}
                {cashback > 0 && (
                  <TableRow>
                    <TableCell sx={{ fontSize: "16px" }}>{t("table.cashback")}</TableCell>
                    <TableCell align="right" sx={{ fontSize: "16px" }}>
                      {`- ${cashback.toFixed(2)} ${data.currency || ""}`.trim()}
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell sx={{ fontSize: "16px" }}>{t("table.total")}</TableCell>
                  <TableCell align="right" sx={{ fontSize: "16px" }}>
                    {data.total.toFixed(2)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}
    </Modal>
  );
};

export default HitoryModal;
